import type { WalletOrder } from '../../features/wallet/api.ts';
import type { MessageKey } from '../i18n/messages.ts';
import { formatLocalTime, localDayKey, localDayShort, localDayShortWithYear } from '../lib/datetime.ts';
import { fmtAmount } from '../lib/format.ts';
import { orderStatusBucket, orderStatusLabelKey as paymentStatusLabelKey } from './orderStatus.ts';

export type UiOrder = {
  id: string;
  kind: 'deposit' | 'withdraw';
  publicId: string;
  providerRef: string;
  providerOrderId: string;
  checkoutUrl: string;
  channelLabel: string;
  channelId: string;
  asset: string;
  network: string;
  status: string;
  amount: number;
  requestedAmount: number;
  receivedAmount: number;
  feeAmount: number;
  payoutAmount: number;
  createdAt: number;
  updatedAt: number;
  paidAt: number;
  expiresAt: number;
};

export type OrderKindFilter = 'deposit' | 'withdraw';
export type OrderStatusFilter = 'all' | 'pending' | 'completed' | 'failed';
export type TimelineState = 'done' | 'current' | 'failed' | 'muted';
export type TimelineRow = { label: string; time: string; state: TimelineState };

export function normalizeOrders(deposits: WalletOrder[], withdrawals: WalletOrder[]): UiOrder[] {
  const depositRows: UiOrder[] = deposits.map((order) => ({
    id: String(order.id),
    kind: 'deposit' as const,
    publicId: String(order.publicId || order.providerRef || ''),
    providerRef: String(order.providerRef || ''),
    providerOrderId: String(order.providerOrderId || order.psp_order_id || ''),
    checkoutUrl: String(order.checkoutUrl || ''),
    channelLabel: String(order.channelLabel || order.network || order.channelId || ''),
    channelId: String(order.channelId || ''),
    asset: String(order.asset || ''),
    network: String(order.network || ''),
    status: String(order.status || ''),
    // receivedAmount is NOT NULL DEFAULT 0 on the backend, so a pending or
    // expired deposit sends receivedAmount: 0 — falling back on `??` never
    // triggers and the hero showed 0 instead of the requested amount.
    amount: Number(order.receivedAmount) > 0 ? Number(order.receivedAmount) : Number(order.requestedAmount ?? 0),
    requestedAmount: Number(order.requestedAmount ?? 0),
    receivedAmount: Number(order.receivedAmount ?? 0),
    feeAmount: 0,
    payoutAmount: 0,
    createdAt: Number(order.createdAt || 0),
    updatedAt: Number(order.updatedAt || 0),
    paidAt: Number(order.paidAt || 0),
    expiresAt: Number(order.expiresAt || 0),
  }));
  const withdrawRows: UiOrder[] = withdrawals.map((order) => ({
    id: String(order.id),
    kind: 'withdraw' as const,
    publicId: String(order.publicId || order.providerRef || ''),
    providerRef: String(order.providerRef || ''),
    providerOrderId: String(order.providerOrderId || order.psp_order_id || ''),
    checkoutUrl: String(order.checkoutUrl || ''),
    channelLabel: String(order.channelLabel || order.network || order.channelId || ''),
    channelId: String(order.channelId || ''),
    asset: String(order.asset || ''),
    network: String(order.network || ''),
    status: String(order.status || ''),
    amount: Number(order.payoutAmount ?? order.requestedAmount ?? 0),
    requestedAmount: Number(order.requestedAmount ?? 0),
    receivedAmount: 0,
    feeAmount: Number(order.feeAmount ?? 0),
    payoutAmount: Number(order.payoutAmount ?? Math.max(0, Number(order.requestedAmount ?? 0) - Number(order.feeAmount ?? 0))),
    createdAt: Number(order.createdAt || 0),
    updatedAt: Number(order.updatedAt || 0),
    paidAt: Number(order.paidAt || 0),
    expiresAt: Number(order.expiresAt || 0),
  }));
  return depositRows.concat(withdrawRows).sort((a, b) => b.createdAt - a.createdAt);
}

export function orderRouteId(order: UiOrder) {
  return String(order.publicId || order.providerRef || order.providerOrderId || `${order.kind}-${order.id}`);
}

export function orderSupportRoute(order: UiOrder) {
  const routeId = encodeURIComponent(orderRouteId(order));
  const status = encodeURIComponent(String(order.status || 'unknown'));
  return `wallet.orders/${order.kind}/${routeId}?status=${status}`;
}

export function findOrderByRouteId(orders: UiOrder[], routeId: string | null) {
  const value = decodeRouteId(routeId);
  if (!value) return null;
  return orders.find((order) => {
    const candidates = [
      orderRouteId(order),
      order.id,
      order.publicId,
      order.providerRef,
      order.providerOrderId,
      `${order.kind}-${order.id}`,
    ];
    return candidates.some((candidate) => decodeRouteId(candidate) === value);
  }) || null;
}

export function decodeRouteId(value: string | null | undefined) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  try {
    return decodeURIComponent(raw).toLowerCase();
  } catch {
    return raw.toLowerCase();
  }
}

export function withdrawFeePercent(order: UiOrder) {
  if (order.requestedAmount <= 0 || order.feeAmount <= 0) return '0%';
  const percent = order.feeAmount / order.requestedAmount * 100;
  return `${fmtAmount(Math.round(percent * 100) / 100)}%`;
}

export function orderTimeline(
  order: UiOrder,
  status: 'completed' | 'pending' | 'failed',
  t: (key: MessageKey) => string,
): TimelineRow[] {
  const middleState: TimelineState = status === 'pending' ? 'current' : status === 'failed' ? 'failed' : 'done';
  const rows: TimelineRow[] = [
    {
      label: t('pedidosTimelineCreated'),
      time: formatLocalTime(order.createdAt),
      state: 'done',
    },
    {
      label: order.kind === 'deposit' ? t('pedidosTimelineGateway') : t('pedidosTimelineReview'),
      time: order.updatedAt ? formatLocalTime(order.updatedAt) : '',
      state: middleState,
    },
  ];
  if (status === 'completed') {
    rows.push({
      label: order.kind === 'deposit' ? t('pedidosTimelineCredited') : t('pedidosTimelineCompleted'),
      time: formatLocalTime(order.paidAt || order.updatedAt || order.createdAt),
      state: 'done',
    });
  } else if (status === 'failed') {
    rows.push({
      label: t(paymentStatusLabelKey(order.status, order.kind)),
      time: formatLocalTime(order.updatedAt || order.createdAt),
      state: 'failed',
    });
  }
  return rows;
}

export function orderStatusFilterLabelKey(status: OrderStatusFilter): MessageKey {
  if (status === 'pending') return 'pedidosStatusPending';
  if (status === 'completed') return 'pedidosStatusCompleted';
  if (status === 'failed') return 'pedidosStatusFailed';
  return 'pedidosStatusAll';
}

export function filterOrders(orders: UiOrder[], kind: OrderKindFilter, status: OrderStatusFilter): UiOrder[] {
  return orders.filter((order) => order.kind === kind && (status === 'all' || orderStatusBucket(order.status, order.kind) === status));
}

export function recentOrders(orders: UiOrder[], limit = 50): UiOrder[] {
  return [...orders].sort((a, b) => b.createdAt - a.createdAt).slice(0, limit);
}

export type DayGroup = { key: string; label: string; orders: UiOrder[] };

export function groupOrdersByDay(orders: UiOrder[], nowSeconds: number, t: (key: MessageKey) => string): DayGroup[] {
  const today = localDayKey(nowSeconds);
  const yesterday = localDayKey(nowSeconds - 86_400);
  const currentYear = today.slice(0, 4);
  const groups = new Map<string, DayGroup>();
  for (const order of [...orders].sort((a, b) => b.createdAt - a.createdAt)) {
    const dayKey = localDayKey(order.createdAt);
    const key = dayKey || 'unknown';
    const label = key === today ? t('historyToday')
      : key === yesterday ? t('historyYesterday')
      : dayKey.slice(0, 4) !== currentYear ? localDayShortWithYear(order.createdAt)
      : localDayShort(order.createdAt);
    const group = groups.get(key) || { key, label, orders: [] };
    group.orders.push(order);
    groups.set(key, group);
  }
  return [...groups.values()];
}
